<template>
  <tr :class="{ 'slice-row--open': slice.is_open }">
    <td class="text-nowrap">{{ slice.period_label }}</td>
    <td>
      <MandateLinkComponent
        v-if="slice.mandate_id !== null"
        :mandateId="slice.mandate_id"
        :title="
          $t('forwards.deals.plan.backed_by_commitment', {
            id: slice.commitment_id,
          })
        "
      >
        {{ slice.mandate_name }}
      </MandateLinkComponent>
      <span v-else class="fw-semibold text-warning-emphasis">
        {{ $t("forwards.deals.plan.open_volume") }}
      </span>
    </td>
    <td class="slice-row__fill">
      <EscrowFillBarComponent
        :escrowed="slice.escrowed_mwh"
        :total="slice.volume_mwh"
        :showShortfall="showShortfall"
        :caption="fillCaption"
      />
    </td>
    <td v-if="canManage" class="text-end">
      <div class="dropdown">
        <!-- `strategy: fixed` lifts the menu out of the card's own clipping, as in the admin
             transactions table. -->
        <button
          type="button"
          class="btn btn-sm btn-ghost"
          data-bs-toggle="dropdown"
          data-bs-config='{"popperConfig":{"strategy":"fixed"}}'
          aria-expanded="false"
          :aria-label="
            $t('forwards.deals.plan.actions.menu', {
              period: slice.period_label,
            })
          "
          :disabled="disabled"
        >
          <FontAwesomeIcon icon="fas fa-ellipsis-v" />
        </button>
        <ul class="dropdown-menu dropdown-menu-end">
          <li v-for="action in actions" :key="action.key">
            <button
              type="button"
              class="dropdown-item"
              :title="action.tooltip"
              :disabled="action.disabled"
              @click="action.run"
            >
              {{ action.label }}
            </button>
          </li>
        </ul>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import MandateLinkComponent from "@/forwards2/components/MandateLinkComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import EscrowFillBarComponent from "@/forwards2/components/EscrowFillBarComponent.vue";
import type {
  SliceAction,
  SliceDialogKind,
} from "@/forwards2/deals/sliceActions";
import type { SliceResponse } from "@/forwards2/forwardsTypes";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface MenuEntry {
  key: string;
  label: string;
  tooltip: string;
  disabled: boolean;
  run: () => void;
}

/**
 * One `(period, volume)` row of a delivery: what backs it, how much is planned, how much of that is
 * already in hand, and every edit that volume admits.
 *
 * The row is where a slice is acted on — one overflow menu per row, whose entries depend on whether
 * the volume is backed (resize, split-move, narrow, remove the backing, pull escrow for the one
 * commitment behind it) or still open (back it, resize it, drop it). Open volume is highlighted
 * rather than hidden: it is what a deal cannot be confirmed with.
 */
const props = withDefaults(
  defineProps<{
    slice: SliceResponse;
    /** Plan and commitment edits — a running deal the actor may manage. */
    canManage?: boolean;
    /** Registry-backed escrow moves, which only a super admin may run. */
    canMoveEscrow?: boolean;
    disabled?: boolean;
  }>(),
  { canManage: false, canMoveEscrow: false, disabled: false }
);

const emit = defineEmits<{
  "slice-action": [action: SliceAction];
}>();

const { t } = useI18n();

const plannedText = computed(() =>
  formatNumberWithSpaces(props.slice.volume_mwh)
);

const missingMwh = computed(() =>
  Math.max(0, props.slice.volume_mwh - props.slice.escrowed_mwh)
);

const showShortfall = computed(
  () => !props.slice.is_open && missingMwh.value > 0
);

/** "1 200 of 2 000 MWh · 800 short" — or "· 800 open" while nothing backs the volume yet. */
const fillCaption = computed(() => {
  const base = t("forwards.common.fill_of_total", {
    filled: formatNumberWithSpaces(props.slice.escrowed_mwh),
    total: plannedText.value,
  });
  if (missingMwh.value === 0) return base;
  const suffix = props.slice.is_open
    ? t("forwards.deals.plan.n_open", {
        mwh: formatNumberWithSpaces(missingMwh.value),
      })
    : t("forwards.deals.plan.n_short", {
        mwh: formatNumberWithSpaces(missingMwh.value),
      });
  return `${base} · ${suffix}`;
});

const actions = computed<MenuEntry[]>(() =>
  props.slice.is_open ? openActions.value : backedActions.value
);

const backedActions = computed<MenuEntry[]>(() => {
  const entries: MenuEntry[] = [];
  if (props.canManage) {
    entries.push(
      entry("resize", "resize", onSlice("resize")),
      entry("move", "split_move", onSlice("move")),
      entry("narrow", "narrow", onSlice("narrow")),
      // Escrow sits against the backing; the backend refuses to drop it while any is attributed.
      props.slice.escrowed_mwh > 0
        ? entry("remove-backing", "remove_backing", () => {}, {
            tooltipKey: "remove_backing_escrowed",
            tooltipParams: {
              mwh: formatNumberWithSpaces(props.slice.escrowed_mwh),
            },
            disabled: true,
          })
        : entry("remove-backing", "remove_backing", onSlice("remove-backing"))
    );
  }
  // A backed slice has exactly one commitment behind it, so the pull needs no further choice.
  const commitmentId = props.slice.commitment_id;
  if (props.canManage && props.canMoveEscrow && commitmentId !== null) {
    entries.push(
      entry("escrow-pull", "pull_to_escrow", () =>
        emit("slice-action", { kind: "escrow-pull", commitmentId })
      )
    );
  }
  return entries;
});

// Open volume is a gap waiting for a mandate; how much is planned is the editor's business.
const openActions = computed<MenuEntry[]>(() =>
  props.canManage ? [entry("back", "back", onSlice("back"))] : []
);

function onSlice(kind: SliceDialogKind): () => void {
  return () => emit("slice-action", { kind, sliceId: props.slice.id });
}

function entry(
  key: string,
  copyKey: string,
  run: () => void,
  options: {
    tooltipKey?: string;
    tooltipParams?: Record<string, string>;
    disabled?: boolean;
  } = {}
): MenuEntry {
  return {
    key,
    label: t(`forwards.deals.plan.actions.${copyKey}`),
    tooltip: t(
      `forwards.deals.plan.actions.${options.tooltipKey ?? copyKey}_tooltip`,
      options.tooltipParams ?? {}
    ),
    disabled: options.disabled ?? false,
    run,
  };
}
</script>

<style scoped lang="scss">
.slice-row--open {
  background: var(--phoenix-warning-bg-subtle);
}

.slice-row__fill {
  min-width: 12rem;
}
</style>
